import React from "react";
import {
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import type { ArchitectReviewPayload } from "../../utils/architectReview";

const DEFAULT_EMPTY_MESSAGE =
  "Standards review comments will appear here after you run a review.";

interface ArchitectReviewPanelProps {
  review: ArchitectReviewPayload | null;
  emptyMessage?: string;
}

const severityColor: Record<string, "error" | "warning" | "info" | "default"> = {
  error: "error",
  warning: "warning",
  info: "info",
};

const ArchitectReviewPanel: React.FC<ArchitectReviewPanelProps> = ({
  review,
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
}) => {
  if (!review) {
    return (
      <Paper variant="outlined" sx={{ p: 2, minHeight: 200 }}>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Standards review
      </Typography>
      {review.summary && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          {review.summary}
          {review.score != null && ` (score: ${review.score})`}
        </Typography>
      )}
      {review.comments.length === 0 ? (
        <Alert severity="success">No issues found.</Alert>
      ) : (
        <List dense disablePadding>
          {review.comments.map((c, i) => (
            <ListItem key={`${c.ruleId}-${i}`} alignItems="flex-start" sx={{ px: 0 }}>
              <Chip
                label={c.severity}
                size="small"
                color={severityColor[c.severity] ?? "default"}
                sx={{ mr: 1, mt: 0.25, textTransform: "capitalize" }}
              />
              <ListItemText
                primary={c.message}
                secondary={[c.ruleId, c.path].filter(Boolean).join(" · ")}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default ArchitectReviewPanel;
